import { captureApiRequestAuthority } from '@/lib/api-request-authority.js';
import { ConfiguredHome } from '@/features/workspace-start-page/ConfiguredHome.js';
import { useStartPage } from '@/features/workspace-start-page/use-start-page.js';
import { isStartPageAccessDenied } from '@/lib/api/workspace-start-page.js';
import { SendRejectedError } from '@/integration/services/integration-api.js';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import {
  FeatureRequestBanner,
  GreetingHero,
  MobileQuickActions,
  QuickActionLauncher,
  RecentFiles,
  useQualitySetupOffer,
} from '@/features/home/index.js';
import {
  CustomizeSheet,
  useQuickActionsLane,
  type CustomizeSheetLane,
} from '@/features/quick-actions/index.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { SecretaryClientLauncher } from '@/features/secretary/SecretaryClientLauncher.js';
import {
  useDeveloperCommandAvailable,
  useDeveloperCommandUnavailable,
} from '@/features/developers/useDeveloperCommandAvailable.js';
import { useInstanceKey } from '@/hooks/useInstanceKey.js';
import { useBreakpoint } from '@/hooks/useMediaQuery.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';
import { reserveConversation } from '@/integration/services/integration-api.js';
import {
  quickActionsQueryOptions,
  type QuickActionPref,
} from '@/lib/api/quick-actions.js';
import { workspacesQueryOptions } from '@/lib/api/workspaces.js';
import { Composer, type ComposerAttachment } from '@/ui/chat/Composer.js';
import type { ComposerDraftBinding } from '@/ui/chat/composer/composer-draft.js';
import { stageLauncherSubmit } from '@/ui/chat/launcher-submit.js';

export interface HomeSearch {
  /** Prefills the composer (e.g. from the "Create via chat" button); does NOT auto-send. */
  prompt?: string;
  /**
   * Opens the New workspace dialog on arrival — the deep link the Secretary's Teams workspace card
   * sends someone to when the workspace they need does not exist yet. Declared here so the route's
   * validation keeps it; the dialog itself belongs to WorkspaceSwitcher, which strips the param
   * after opening. The number variant exists because the router's default parser JSON-parses each
   * value — the literal `?create_workspace=1` arrives as the number 1, and validation must return
   * it UNCHANGED or the router rewrites the URL to the re-stringified value on load.
   */
  create_workspace?: '1' | 1;
}

/**
 * Exported for its test — the deep-link contract here is subtle enough to have broken once.
 */
export function validateHomeSearch(raw: Record<string, unknown>): HomeSearch {
  // Trim so a whitespace-only deep-link (?prompt=%20) doesn't prefill the composer with blanks.
  const trimmed = typeof raw.prompt === 'string' ? raw.prompt.trim() : '';
  return {
    prompt: trimmed.length > 0 ? trimmed : undefined,
    /**
     * The router's default search parser JSON-parses each value, so the literal `?create_workspace=1`
     * arrives here as the NUMBER 1, not the string '1'. Dropping it would make the router rewrite the
     * URL to the validated search before the sidebar ever mounts — the deep link then dies without a
     * trace (found live: the dialog never opened and the param vanished on load). Kept verbatim, not
     * normalized, so the URL is never rewritten either.
     */
    create_workspace:
      raw.create_workspace === '1' || raw.create_workspace === 1
        ? raw.create_workspace
        : undefined,
  };
}

export const Route = createFileRoute('/home')({
  component: HomePage,
  validateSearch: validateHomeSearch,
});

function HomePage(): JSX.Element {
  const { prompt: prefillPrompt } = useSearch({ from: '/home' });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const lane = useQuickActionsLane();
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const startPage = useStartPage(selectedWorkspaceId);
  const workspaceDirectory = useQuery({
    ...workspacesQueryOptions(),
    enabled: Boolean(startPage.actor && !selectedWorkspaceId),
  });
  const accessDenied = isStartPageAccessDenied(startPage.error);
  const pageKey = `${startPage.actor}:${selectedWorkspaceId}:${startPage.featureEnabled}`;
  const initialPage = useRef<{
    key: string;
    resolved: boolean;
    data: typeof startPage.data;
  }>({
    key: pageKey,
    resolved: false,
    data: undefined,
  });
  if (initialPage.current.key !== pageKey)
    initialPage.current = { key: pageKey, resolved: false, data: undefined };
  if (
    !initialPage.current.resolved &&
    (startPage.data || (startPage.isError && !accessDenied))
  ) {
    initialPage.current = {
      key: pageKey,
      resolved: true,
      data: startPage.data,
    };
  }
  const displayedPage = startPage.featureEnabled
    ? initialPage.current.data
    : undefined;
  const qualitySetupOffer = useQualitySetupOffer(
    selectedWorkspaceId ?? undefined,
  );
  const qualitySetup =
    startPage.data?.can_launch === false ? null : qualitySetupOffer;
  const { data: quickActions } = useQuery(
    quickActionsQueryOptions(lane.workspaceId),
  );
  const sheetLane: CustomizeSheetLane = {
    workspaceId: lane.workspaceId ?? null,
    workspaceName: lane.workspaceName,
    source: quickActions?.source ?? 'default',
  };
  const navigate = useNavigate();
  const setPendingConversationId = useUiStore(
    (s) => s.setPendingConversationId,
  );
  const isMobile = useBreakpoint() === 'mobile';
  const developerCommandEnabled = useDeveloperCommandAvailable();
  const developerCommandUnavailable = useDeveloperCommandUnavailable();
  const newChatDraft = useNewChatDraft(startPage.actor);
  const hydrated = useUiStore((state) => state.hasHydrated);
  const reservationScope = useRef(newChatDraft.instance);
  reservationScope.current = newChatDraft.instance;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** A reservation belongs to this account/workspace visit and is shared by all draft uploads. */
  const reservationFlight = useRef<{
    instance: number;
    promise: Promise<string>;
  } | null>(null);
  const ensureConversationId = useCallback(async (): Promise<string> => {
    const workspaceId = useUiStore.getState().selectedWorkspaceId;
    const ownerInstance = newChatDraft.instance;
    const authority = captureApiRequestAuthority(
      () =>
        mounted.current &&
        reservationScope.current === ownerInstance &&
        useUiStore.getState().selectedWorkspaceId === workspaceId,
    );
    try {
      if (newChatDraft.reservation.current)
        return newChatDraft.reservation.current;
      if (reservationFlight.current?.instance === ownerInstance)
        return await reservationFlight.current.promise;
      const promise = reserveConversation({
        workspaceId: workspaceId ?? undefined,
      }).then((created) => {
        authority.assertCurrent();
        newChatDraft.setReservation(created.id);
        return created.id;
      });
      reservationFlight.current = { instance: ownerInstance, promise };
      try {
        return await promise;
      } finally {
        if (reservationFlight.current?.promise === promise)
          reservationFlight.current = null;
      }
    } finally {
      authority.release();
    }
  }, [newChatDraft.instance, newChatDraft.setReservation]);

  // Reaching /home (logo / New chat / mobile + button) wipes the active/streaming conversation ids.
  // Without this, a stale activeConversationId from a prior `/chats/$id` session leaked through
  // `useChat` when the user hit a Quick Action card here, causing the sidebar to keep the previous
  // chat highlighted and ChatView to render its messages for ~1-5 s until the server emitted the new
  // conversationId event. Done during render via state-sync to avoid the no-setState-in-effect lint.
  const [didClearProject, setDidClearProject] = useState(false);
  if (!didClearProject) {
    setDidClearProject(true);
    // Drop any conversation id reserved by a prior launcher session the user abandoned without
    // sending, so a fresh chat here doesn't reuse (and append into) that stale conversation.
    setPendingConversationId(null);
    useChatSessionStore.getState().setActiveConversationId(null);
    useChatSessionStore.getState().setStreamingConvId(null);
  }

  function handleSubmit(
    body: string,
    _tag: string | null,
    attachments: ComposerAttachment[],
    skillIds: string[],
  ): void {
    if (accessDenied || startPage.data?.can_launch === false)
      throw new SendRejectedError(
        'This workspace is not available for new chats.',
      );
    if (!stageLauncherSubmit(body, attachments, skillIds)) return;
    setPendingConversationId(newChatDraft.reservation.current);
    void navigate({ to: '/chats' });
  }

  /** A native tool action preserves the draft, so it must not consume that draft's reservation. */
  async function handleToolSubmit(prompt: string): Promise<void> {
    if (accessDenied || startPage.data?.can_launch === false)
      throw new SendRejectedError(
        'This workspace is not available for new chats.',
      );
    const ownerInstance = newChatDraft.instance;
    const authority = captureApiRequestAuthority(
      () =>
        mounted.current &&
        reservationScope.current === ownerInstance &&
        useUiStore.getState().selectedWorkspaceId === selectedWorkspaceId,
    );
    try {
      const conversation = await reserveConversation({
        workspaceId: selectedWorkspaceId ?? undefined,
      });
      authority.assertCurrent();
      if (!stageLauncherSubmit(prompt, [], [])) return;
      setPendingConversationId(conversation.id);
      void navigate({ to: '/chats' });
    } finally {
      authority.release();
    }
  }

  const renderQuickActions = (
    onLaunch: ((action: QuickActionPref) => void) | undefined = startPage.data
      ?.can_launch === false
      ? () => undefined
      : undefined,
  ) => (
    <>
      {isMobile ? (
        <MobileQuickActions
          workspaceId={lane.workspaceId}
          qualitySetup={qualitySetup}
          onLaunch={onLaunch}
          onCustomize={
            lane.workspaceId !== undefined
              ? () => setCustomizeOpen(true)
              : undefined
          }
        />
      ) : (
        <QuickActionLauncher
          workspaceId={lane.workspaceId}
          qualitySetup={qualitySetup}
          onLaunch={onLaunch}
          onCustomize={
            lane.workspaceId !== undefined
              ? () => setCustomizeOpen(true)
              : undefined
          }
        />
      )}
      <CustomizeSheet
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        current={quickActions?.items ?? []}
        pinned={quickActions?.pinned}
        lane={sheetLane}
      />
    </>
  );

  if (startPage.identityLoaded && !startPage.actor)
    return (
      <div role="alert" className="p-8">
        <p>Your account could not load. Reload the page to try again.</p>
        <button
          type="button"
          className="mt-4 underline"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    );

  if (hydrated && startPage.actor && !selectedWorkspaceId) {
    const message = workspaceDirectory.isError
      ? 'Your workspaces could not load. Try again.'
      : workspaceDirectory.fetchStatus === 'paused'
        ? 'You are offline. Reconnect to load your workspaces.'
        : workspaceDirectory.isPending
          ? 'Loading your workspaces…'
          : workspaceDirectory.data?.length === 0
            ? 'You have no workspaces yet. Create one to start a chat.'
            : 'Select a workspace from the workspace menu to start a chat.';
    return (
      <div
        role={workspaceDirectory.isError ? 'alert' : 'status'}
        className="p-8"
      >
        <p>{message}</p>
        {workspaceDirectory.isError ? (
          <button
            type="button"
            className="mt-4 underline"
            onClick={() => void workspaceDirectory.refetch()}
          >
            Retry
          </button>
        ) : workspaceDirectory.data?.length === 0 ? (
          <button
            type="button"
            className="mt-4 underline"
            onClick={() =>
              void navigate({
                to: '/home',
                search: (previous) => ({ ...previous, create_workspace: 1 }),
              })
            }
          >
            Create workspace
          </button>
        ) : null}
      </div>
    );
  }

  if (
    !hydrated ||
    !selectedWorkspaceId ||
    !startPage.actor ||
    (startPage.isPending && !initialPage.current.resolved)
  )
    return (
      <p role="status" className="p-8">
        {startPage.fetchStatus === 'paused'
          ? 'You are offline. Reconnect to load the workspace start page.'
          : 'Loading workspace start page…'}
      </p>
    );

  if (
    !accessDenied &&
    displayedPage?.document &&
    selectedWorkspaceId &&
    startPage.actor
  ) {
    return (
      <ConfiguredHome
        key={`${startPage.actor}:${selectedWorkspaceId}:${displayedPage.published_version}`}
        workspaceId={selectedWorkspaceId}
        actor={startPage.actor}
        version={displayedPage.published_version!}
        page={displayedPage.document}
        canLaunch={startPage.data?.can_launch === true}
        renderQuickActions={renderQuickActions}
        composer={{
          draft: newChatDraft.binding,
          ensureConversationId,
          initialValue: prefillPrompt,
          developerCommandEnabled,
          developerCommandUnavailable,
          focusOnMount: !isMobile,
        }}
      />
    );
  }
  if (accessDenied)
    return (
      <div role="alert" className="p-8">
        <p>
          This workspace is no longer available. Select another workspace or
          retry.
        </p>
        <button
          className="mt-4 underline"
          onClick={() => void startPage.refetch()}
        >
          Retry
        </button>
      </div>
    );

  const pageNotice = startPage.isError ? (
    <div role="status" className="p-3 text-sm text-muted-foreground">
      The workspace start page could not load. You can still start a standard
      chat.{' '}
      <button className="underline" onClick={() => void startPage.refetch()}>
        Retry
      </button>
    </div>
  ) : null;
  if (isMobile) {
    return (
      <div className="home-fluid flex h-full flex-col px-4 pb-4">
        {pageNotice}
        <div className="home-hero-area flex flex-1 items-center justify-center">
          <GreetingHero logoOnly />
        </div>

        <div className="flex flex-col gap-4">
          {renderQuickActions()}
          <Composer
            key={newChatDraft.instance}
            draft={newChatDraft.binding}
            className=""
            disabled={startPage.data?.can_launch === false}
            onSubmit={handleSubmit}
            onToolSubmit={handleToolSubmit}
            ensureConversationId={ensureConversationId}
            initialValue={prefillPrompt}
            developerCommandEnabled={developerCommandEnabled}
            developerCommandUnavailable={developerCommandUnavailable}
          />
          <FeatureRequestBanner />
          <SecretaryClientLauncher />
        </div>
      </div>
    );
  }

  return (
    <div
      className="home-fluid flex min-h-full flex-col items-center justify-center px-4"
      style={{ paddingBottom: 'var(--home-offset)' }}
    >
      <div
        className="w-full"
        style={{
          maxWidth: 'min(82%, 48rem)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--home-gap-sections)',
          marginBottom: '6rem',
        }}
      >
        {pageNotice}
        <GreetingHero />

        <Composer
          key={newChatDraft.instance}
          draft={newChatDraft.binding}
          className=""
          disabled={startPage.data?.can_launch === false}
          onSubmit={handleSubmit}
          onToolSubmit={handleToolSubmit}
          ensureConversationId={ensureConversationId}
          initialValue={prefillPrompt}
          focusOnMount
          developerCommandEnabled={developerCommandEnabled}
          developerCommandUnavailable={developerCommandUnavailable}
        />

        {renderQuickActions()}

        <SecretaryClientLauncher />

        <RecentFiles />
      </div>

      <FeatureRequestBanner />
    </div>
  );
}

/** The local reservation stays with its account/workspace draft until an explicit chat handoff. */
function useNewChatDraft(actor: string | null) {
  const workspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const instance = useInstanceKey(`${actor}:${workspaceId}`);
  const [stored, setStored] = useState<{ instance: number; id: string | null }>(
    { instance, id: null },
  );
  const reservation = useRef<string | null>(null);
  const currentInstance = useRef(instance);
  currentInstance.current = instance;
  if (stored.instance !== instance) setStored({ instance, id: null });
  reservation.current = stored.instance === instance ? stored.id : null;
  const setReservation = useCallback(
    (id: string | null) => {
      if (currentInstance.current !== instance) return;
      reservation.current = id;
      setStored({ instance, id });
    },
    [instance],
  );
  const binding: ComposerDraftBinding = {
    key:
      actor && workspaceId ? { scope: 'new_chat', scopeId: workspaceId } : null,
    reservedConversationId: reservation.current,
    onReservedConversationChange: setReservation,
  };
  return { instance, binding, reservation, setReservation };
}
